import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import SideBar from './SideBar';
import HeaderSection from './HeaderSection';
import {
    getAdminPlans,
    createAdminPlan,
    updateAdminPlan,
    deleteAdminPlan,
    ADMIN_PLAN_LIMIT_VALUE,
} from '../../utils/admin/dashboardApi';

const gradientBtn = {
    border: '1px solid #f0c100',
    color: '#111',
    background: 'linear-gradient(131deg, #fe8100 -4.12%, #f5ff00 100%)',
    fontWeight: 700,
    borderRadius: '8px',
};

const outlineBtn = {
    border: `1px solid #dcdcdc`,
    color: '#16182D',
    backgroundColor: '#fff',
};

/** DB `plans.type`: enum('Monthly','Yearly') */
const BILLING_TYPES = ['Monthly', 'Yearly'];

/** DB `plans.payment_type`: enum('Free','Paid') — NOT gateway names like Stripe */
const PAYMENT_TYPES = ['Free', 'Paid'];

const normalizeBillingType = (v) => (BILLING_TYPES.includes(v) ? v : 'Monthly');

const normalizePaymentType = (v, price) => {
    if (PAYMENT_TYPES.includes(v)) return v;
    const n = Number(price) || 0;
    return n > 0 ? 'Paid' : 'Free';
};

const emptyForm = () => ({
    name: '',
    price: 0,
    type: 'Monthly',
    payment_type: 'Free',
    views: 0,
    widgets_count: 0,
});

export default function Plans() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);

    const brandColors = {
        logoBlueDark: '#16182D',
        logoBlue: '#1f3f95',
        logoBlueLight: '#5f79c7',
        logoYellow: '#f5c400',
        borderSoft: '#dcdcdc',
        surfaceSoft: '#f6f6f6',
    };

    const loadPlans = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAdminPlans();
            if (res?.status === true) {
                setPlans(Array.isArray(res.data) ? res.data : []);
            } else {
                setPlans([]);
                toast.error(res?.message || 'Could not load plans');
            }
        } catch (e) {
            setPlans([]);
            toast.error(e?.message || 'Could not load plans');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPlans();
    }, [loadPlans]);

    useEffect(() => {
        if (!showModal) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [showModal]);

    useEffect(() => {
        if (!showModal) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setShowModal(false);
                setEditingId(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showModal]);

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
    };

    const filteredPlans = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return plans;
        return plans.filter((p) => {
            const name = String(p.name || '').toLowerCase();
            const type = String(p.type || '').toLowerCase();
            const pay = String(p.payment_type || '').toLowerCase();
            return name.includes(q) || type.includes(q) || pay.includes(q);
        });
    }, [plans, searchTerm]);

    const totalPlans = plans.length;
    const paidPlansCount = plans.filter((p) => Number(p.price) > 0).length;
    const freePlansCount = plans.filter((p) => !Number(p.price)).length;

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setShowModal(true);
    };

    const openEdit = (plan) => {
        const price = Number(plan.price) || 0;
        setEditingId(plan.id);
        setForm({
            name: plan.name ?? '',
            price,
            type: normalizeBillingType(plan.type),
            payment_type: normalizePaymentType(plan.payment_type, price),
            views: plan.views ?? '',
            widgets_count: plan.widgets_count ?? '',
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error('Plan name is required');
            return;
        }
        setSaving(true);
        try {
            const price = Number(form.price) || 0;
            /* DB enum `limit` — only 'Unlimited'; always send static (views/widgets are separate columns). */
            const payload = {
                name: form.name.trim(),
                price,
                type: normalizeBillingType(form.type),
                payment_type: normalizePaymentType(form.payment_type, price),
                limit: ADMIN_PLAN_LIMIT_VALUE,
                views: form.views === '' || form.views === undefined ? '0' : String(form.views),
                widgets_count:
                    form.widgets_count === '' || form.widgets_count === undefined
                        ? '0'
                        : String(form.widgets_count),
            };
            if (editingId) {
                await updateAdminPlan(editingId, payload);
                toast.success('Plan updated');
            } else {
                await createAdminPlan(payload);
                toast.success('Plan created');
            }
            setShowModal(false);
            setEditingId(null);
            loadPlans();
        } catch (err) {
            toast.error(err?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (plan) => {
        if (
            !window.confirm(
                `Delete plan "${plan.name}"? This cannot be undone if no users or payments reference it.`
            )
        ) {
            return;
        }
        try {
            await deleteAdminPlan(plan.id);
            toast.success('Plan deleted');
            loadPlans();
        } catch (err) {
            toast.error(err?.message || 'Delete failed');
        }
    };

    return (
        <div className="container-scroller admin-dashboard-page">
            <SideBar />
            <div className="main-panel">
                <div className="content-wrapper">
                    <HeaderSection />
                    <div className="admin-customers-page admin-plans-page">
                        <div className="mt-4 px-4 pb-4 w-100 customers-page-body">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 customers-header-row">
                                <h3 className="mb-0">Plans Management</h3>
                                <button type="button" className="btn btn-sm px-3 py-2" style={gradientBtn} onClick={openCreate}>
                                    + Add plan
                                </button>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <div className="card border-0 shadow-sm">
                                        <div
                                            className="card-body py-3"
                                            style={{ borderTop: `3px solid ${brandColors.logoBlueDark}` }}
                                        >
                                            <p className="text-muted mb-1">Total Plans</p>
                                            <h5 className="mb-0" style={{ color: brandColors.logoBlueDark }}>
                                                {totalPlans}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 shadow-sm">
                                        <div
                                            className="card-body py-3"
                                            style={{ borderTop: `3px solid ${brandColors.logoYellow}` }}
                                        >
                                            <p className="text-muted mb-1">Paid plans (price &gt; 0)</p>
                                            <h5 className="mb-0" style={{ color: brandColors.logoYellow }}>
                                                {paidPlansCount}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 shadow-sm">
                                        <div
                                            className="card-body py-3"
                                            style={{ borderTop: `3px solid ${brandColors.logoBlueLight}` }}
                                        >
                                            <p className="text-muted mb-1">Free plans</p>
                                            <h5 className="mb-0" style={{ color: brandColors.logoBlueLight }}>
                                                {freePlansCount}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card border-0 shadow-sm mb-3">
                                <div className="card-body" style={{ backgroundColor: brandColors.surfaceSoft }}>
                                    <input
                                        type="search"
                                        className="form-control admin-plans-search"
                                        placeholder="Search plans…"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        aria-label="Search plans"
                                    />
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-5 text-muted">Loading plans…</div>
                            ) : (
                                <div className="table-responsive plans-table-wrap">
                                    <table className="table table-sm table-hover table-bordered align-middle admin-plans-table">
                                        <thead style={{ backgroundColor: brandColors.logoBlueDark }}>
                                            <tr>
                                                <th className="text-white">#</th>
                                                <th className="text-white">Name</th>
                                                <th className="text-white">Price</th>
                                                <th className="text-white">Type</th>
                                                <th className="text-white">Payment</th>
                                                <th className="text-white">Limit</th>
                                                <th className="text-white">Views</th>
                                                <th className="text-white">Widgets</th>
                                                <th className="text-white">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPlans.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="text-center py-4 text-muted">
                                                        No plans found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredPlans.map((plan, index) => (
                                                    <tr key={plan.id}>
                                                        <td>{index + 1}</td>
                                                        <td className="fw-semibold">{plan.name}</td>
                                                        <td>${Number(plan.price) || 0}</td>
                                                        <td>{plan.type || '—'}</td>
                                                        <td>{plan.payment_type || '—'}</td>
                                                        <td>{plan.limit ?? '—'}</td>
                                                        <td>{plan.views ?? '—'}</td>
                                                        <td>{plan.widgets_count ?? '—'}</td>
                                                        <td>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm"
                                                                    style={{
                                                                        border: '1px solid #b7d6ff',
                                                                        color: '#006dff',
                                                                        backgroundColor: '#F2F8FF',
                                                                    }}
                                                                    onClick={() => openEdit(plan)}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDelete(plan)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showModal &&
                createPortal(
                    <div
                        className="admin-plans-modal-root"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="admin-plans-modal-title"
                        tabIndex={-1}
                    >
                        <button
                            type="button"
                            className="admin-plans-modal-backdrop"
                            aria-label="Close dialog"
                            tabIndex={-1}
                            onClick={closeModal}
                        />
                        <div className="admin-plans-modal-panel" role="document">
                            <header className="admin-plans-modal-header">
                                <div>
                                    <h2 id="admin-plans-modal-title" className="admin-plans-modal-title">
                                        {editingId ? 'Edit plan' : 'Add plan'}
                                    </h2>
                                    <p className="admin-plans-modal-subtitle">
                                        {editingId ? 'Update subscription details' : 'Create a new subscription tier'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="admin-plans-modal-close"
                                    aria-label="Close"
                                    onClick={closeModal}
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </header>
                            <form className="admin-plans-modal-form" onSubmit={handleSubmit}>
                                <div className="admin-plans-modal-body">
                                    <div className="admin-plans-field">
                                        <label className="admin-plans-label" htmlFor="plan-name-input">
                                            Plan name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="plan-name-input"
                                            className="form-control admin-plans-input"
                                            value={form.name}
                                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                            required
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="row g-2 admin-plans-field-row">
                                        <div className="col-12 col-md-6">
                                            <label className="admin-plans-label" htmlFor="plan-price-input">
                                                Price (USD)
                                            </label>
                                            <input
                                                id="plan-price-input"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="form-control admin-plans-input"
                                                value={form.price}
                                                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="admin-plans-label" htmlFor="plan-billing-select">
                                                Billing cycle
                                            </label>
                                            <select
                                                id="plan-billing-select"
                                                className="form-select admin-plans-input"
                                                value={form.type}
                                                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                                            >
                                                {BILLING_TYPES.map((t) => (
                                                    <option key={t} value={t}>
                                                        {t}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="admin-plans-label" htmlFor="plan-pay-select">
                                                Plan access
                                            </label>
                                            <select
                                                id="plan-pay-select"
                                                className="form-select admin-plans-input"
                                                value={form.payment_type}
                                                onChange={(e) =>
                                                    setForm((f) => ({ ...f, payment_type: e.target.value }))
                                                }
                                            >
                                                {PAYMENT_TYPES.map((t) => (
                                                    <option key={t} value={t}>
                                                        {t}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="admin-plans-label" htmlFor="plan-views-input">
                                                Views allowance
                                            </label>
                                            <input
                                                id="plan-views-input"
                                                type="number"
                                                min="0"
                                                className="form-control admin-plans-input"
                                                value={form.views}
                                                onChange={(e) => setForm((f) => ({ ...f, views: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="admin-plans-label" htmlFor="plan-widgets-input">
                                                Widgets count
                                            </label>
                                            <input
                                                id="plan-widgets-input"
                                                type="number"
                                                min="0"
                                                className="form-control admin-plans-input"
                                                value={form.widgets_count}
                                                onChange={(e) =>
                                                    setForm((f) => ({ ...f, widgets_count: e.target.value }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <footer className="admin-plans-modal-footer">
                                    <button
                                        type="button"
                                        className="admin-plans-modal-btn admin-plans-modal-btn--secondary"
                                        onClick={closeModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="admin-plans-modal-btn admin-plans-modal-btn--primary"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving…' : editingId ? 'Update plan' : 'Create plan'}
                                    </button>
                                </footer>
                            </form>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
